export const lottieIcons = {
  'loader': () => import(/* @vite-ignore */ './loader.json').then((m) => m.default),
};

export const lottieIconNames = ["loader"];

export type LottieIconName = 'loader';

type LottieColor = string | [number, number, number];

const baseCache = new Map<string, unknown>();
const colorCache = new Map<string, unknown>();

function normalizeColorToRgb01(color: LottieColor | undefined): [number, number, number] | null {
  if (!color) {
    return null;
  }

  if (Array.isArray(color)) {
    const max = Math.max(...color);
    if (!Number.isFinite(max)) {
      return null;
    }
    return max > 1 ? (color.map((value) => value / 255) as [number, number, number]) : color;
  }

  if (typeof color !== 'string') {
    return null;
  }

  const trimmed = color.trim().toLowerCase();

  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    if (full.length !== 6) {
      return null;
    }
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some((value) => Number.isNaN(value))) {
      return null;
    }
    return [r / 255, g / 255, b / 255];
  }

  if (trimmed.startsWith('rgb')) {
    const matches = trimmed.match(/rgba?\(([^)]+)\)/);
    if (!matches) {
      return null;
    }
    const parts = matches[1]
      .split(',')
      .map((value) => Number.parseFloat(value.trim()))
      .filter((value) => Number.isFinite(value));
    if (parts.length < 3) {
      return null;
    }
    const rgb = parts.slice(0, 3);
    const max = Math.max(...rgb);
    return max > 1 ? (rgb.map((value) => value / 255) as [number, number, number]) : (rgb as [number, number, number]);
  }

  return null;
}

function cloneLottieData<T>(data: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(data);
  }

  return JSON.parse(JSON.stringify(data)) as T;
}

function applyColor(node: unknown, rgb: [number, number, number]) {
  if (!node || typeof node !== 'object') {
    return;
  }

  const record = node as Record<string, any>;
  const colorNode = record.c;

  if (colorNode && colorNode.k) {
    const keyframes = colorNode.k;
    if (Array.isArray(keyframes)) {
      if (keyframes.length === 3 && keyframes.every((value) => typeof value === 'number')) {
        colorNode.k = rgb;
      } else if (keyframes.length && typeof keyframes[0] === 'object') {
        keyframes.forEach((frame: Record<string, any>) => {
          if (Array.isArray(frame.s)) {
            frame.s = rgb;
          }
          if (Array.isArray(frame.e)) {
            frame.e = rgb;
          }
        });
      }
    }
  }

  Object.values(record).forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => applyColor(entry, rgb));
      return;
    }

    if (value && typeof value === 'object') {
      applyColor(value, rgb);
    }
  });
}

export async function getLottieData(name: LottieIconName, color?: LottieColor) {
  const loader = (lottieIcons as Record<string, () => Promise<unknown>>)[name];
  if (!loader) {
    return null;
  }

  let base = baseCache.get(name);
  if (!base) {
    base = await loader();
    baseCache.set(name, base);
  }

  const rgb = normalizeColorToRgb01(color);
  if (!rgb || !base || typeof base !== 'object') {
    return base;
  }

  const cacheKey = name + ":" + rgb.join(",");
  const cached = colorCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const clone = cloneLottieData(base);
  applyColor(clone, rgb);
  colorCache.set(cacheKey, clone);
  return clone;
}
